import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import vector from "../assests/vector.svg";
import message from "../assests/message.png";
import bitmoji from "../assests/bitmoji.jpg";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faXmark } from "@fortawesome/free-solid-svg-icons";
import axiosInstance from "../utils/axios-instance";
import GetUser from "../utils/get-user";
import { toast } from "react-toastify";
import { ParseErrors } from "../utils/parse-errors";

// ----------------------- NOTE HTML TO PLAIN TEXT, LINE BREAKS KEPT ------------------------------------
const noteText = (html: string = "") =>
  new DOMParser().parseFromString(html.replace(/<br\s*\/?>|<\/p>/gi, "\n"), "text/html").body.textContent?.trim() || "";

interface INotesModel {
  onToggle: () => void;
  data: {
    policyNumber: string;
  };
}

interface Message {
  branch: string;
  clientNumber: number;
  policyLine: number;
  policyReference: string;
  createdOn: string;
  agent: {
    name: string;
    code: string;
  };
  notes: string;
}

const NotesModal = ({ onToggle, data }: INotesModel) => {
  const user = GetUser();
  const bottomRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState<{
    totalCount: number;
    data: Message[];
  }>({ totalCount: 0, data: [] });
  const [page, setPage] = useState(0);
  const [limit] = useState(10);
  const [newMessage, setNewMessage] = useState("");

  //  ------------------------------------------------------------------------------------ HANDLE SEND MESSAGE -------------------------------------------------------------------------------------

  const handleSendMessage = async () => {
    if (newMessage.trim()) {
      setSending(true);
      try {
        const res = await axiosInstance.post(`/notes/${data?.policyNumber}`, {
          notes: newMessage,
        });
        setMessages((prev) => ({
          totalCount: prev.totalCount + 1,
          data: [...prev.data, res.data.data],
        }));
        setNewMessage("");
        setSending(false);
      } catch (error) {
        console.log(error);
        setSending(false);
      }
    }
  };

  //  ------------------------------------------------------------------------------------ FIND NOTES -------------------------------------------------------------------------------------

  useEffect(() => {
    const findNotes = async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get(
          `/notes/${data?.policyNumber}?page=${page}&limit=${limit}`
        );
        setMessages(res.data);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };

    data?.policyNumber && !messages.totalCount && findNotes();
  }, [data?.policyNumber, limit, page]);

  // ------------------------------------------------------------------------------------ CARP NEW NOTES ------------------------------------------------------------------------------------
  const carpNewMessages = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `/notes/${data?.policyNumber}?page=${page + limit}&limit=${limit}`
      );
      setPage(page + limit);
      setMessages((prev) => ({
        totalCount: res.data.totalCount,
        data: [...res.data.data, ...prev.data],
      }));
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
      toast.error(ParseErrors(error));
    }
  };

  // -------------------------------------------------- SCROLL TO BOTTOM ----------------------------------------------------------------

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages.totalCount]);

  // ------------------------------------------ SCROLL LOCK + ESC TO CLOSE -----------------------------------------------
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onToggle();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onToggle]);

  return createPortal(
    <div
      className="w-screen h-screen fixed inset-0 bg-[color:var(--shadow-modal-scrim)] backdrop-blur-sm flex items-center justify-center z-[99999] animate-fade-in"
      onClick={onToggle}
    >
      <div
        className="w-full bg-surface border border-edge shadow-overlay rounded-2xl max-w-[600px] h-[80vh] flex flex-col overflow-hidden animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/*---------------------------------------------------------------------------------- Notes Header -------------------------------------------------------------------------------------*/}
        <div className="w-full p-4 flex bg-surface-elevated justify-between items-center border-b border-edge-faint">
          <h3 className="bg-gradient-heading bg-clip-text text-transparent uppercase text-md font-bold">
            Policy Notes
          </h3>
          <button
            onClick={onToggle}
            className="bg-gradient-to-r from-accent to-accent-bright border border-white/10 shadow-lg shadow-indigo-500/20 hover:shadow-glow-sm hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-2 text-sm py-2 px-4 font-normal text-md rounded-lg text-white"
          >
            <FontAwesomeIcon icon={faXmark} size="1x" />
          </button>
        </div>

        {/*---------------------------------------------------------------------------------- Messages -------------------------------------------------------------------------------------*/}
        <div className="w-full flex h-full overflow-auto relative">
          {loading && !messages?.totalCount ? (
            <div className="w-full flex items-center justify-center py-2 absolute top-0 left-0 h-full bg-surface text-text-secondary">
              <FontAwesomeIcon icon={faSpinner} spin size="6x" />
            </div>
          ) : null}
          {messages.data.length ? (
            <div className="w-[95%] mx-auto p-2">
              {page <= messages.totalCount ? (
                <button
                  className="text-xs text-light-gray hover:text-text-primary transition-colors justify-center items-center flex my-4 cursor-pointer mx-auto"
                  onClick={carpNewMessages}
                  disabled={loading}
                >
                  Load Recent Messages{" "}
                  {loading ? (
                    <FontAwesomeIcon icon={faSpinner} spin className="ml-2" />
                  ) : null}
                </button>
              ) : null}
              {messages.data.map((msg, index) => {
                let isSender =
                  msg.agent.code === user.code || msg.agent.code === "DS";
                return (
                  <div key={index} className="mb-6">
                    {!isSender ? (
                      <span
                        className={clsx(
                          "text-xs flex text-text-secondary justify-start ml-[52px] mb-[-2px]"
                        )}
                      >
                        {msg?.agent?.name}
                      </span>
                    ) : null}
                    <div
                      className={clsx(
                        "message_box flex justify-start items-end mb-2",
                        isSender && "justify-end"
                      )}
                    >
                      {!isSender && (
                        <img
                          className="w-10 h-10 rounded-full mr-3"
                          src={message}
                          alt="bot"
                        />
                      )}
                      <div
                        className={clsx(
                          "py-3 px-4 text-sm bg-overlay-mid border border-edge-faint rounded-xl !text-text-primary",
                          isSender &&
                            "!bg-gradient-to-br !from-accent !to-accent-bright !border-transparent py-1 !text-white px-8 shadow-glow-sm"
                        )}
                      >
                        <div className="space-y-2 whitespace-pre-line">
                          {noteText(msg.notes)}
                        </div>
                      </div>
                    </div>
                    <span
                      className={clsx(
                        "text-xs flex text-light-gray justify-start ml-[54px] mt-[-12px]",
                        isSender && "justify-end mt-[6px]"
                      )}
                    >
                      {moment(msg.createdOn).format("YYYY-MM-DD")}
                    </span>
                  </div>
                );
              })}
              <div ref={bottomRef}></div>
            </div>
          ) : null}
        </div>

        {/*---------------------------------------------------------------------------------- Input Field -------------------------------------------------------------------------------------*/}
        <div className="p-4 bg-surface-elevated justify-around border-t border-edge-faint flex items-center">
          <button className="bg-[transparent] border-none outline-none">
            <img className="w-10 h-10 rounded-full" src={bitmoji} alt="send" />
          </button>
          <div className="flex w-[90%]">
            <input
              disabled={sending}
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              className="flex-grow bg-input-bg h-[45px] text-sm px-6 border border-input-border focus:border-indigo-400/40 focus:ring-1 focus:ring-indigo-400/30 focus:outline-none transition-all duration-200 text-text-primary placeholder:text-label-muted rounded-lg mx-2"
              placeholder="Type Here..."
            />
          </div>
          <button
            onClick={handleSendMessage}
            className="bg-[transparent] border-none outline-none"
            disabled={sending || loading}
          >
            {sending ? (
              <FontAwesomeIcon icon={faSpinner} spin size="1x" />
            ) : (
              <img src={vector} alt="send" />
            )}
          </button>
        </div>

        {/*---------------------------------------------------------------------------------- Input Field -------------------------------------------------------------------------------------*/}
      </div>
    </div>,
    document.body
  );
};

export default NotesModal;
